import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as claimedItemsService from "../services/claimedItemsService";
import * as wishlistItemsService from "../services/wishlistItemsService";

export function useClaimedItemsQuery(userId) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return undefined;
    const unsubscribe = claimedItemsService.subscribeToClaimedUpdates(userId, () => {
      queryClient.invalidateQueries({ queryKey: ["claimedItems", userId] });
    });
    return unsubscribe;
  }, [userId, queryClient]);

  return useQuery({
    queryKey: ["claimedItems", userId],
    queryFn: () => claimedItemsService.fetchClaimedItems(userId),
    enabled: !!userId,
  });
}

function useClaimedItemsMutation(userId, mutationFn) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claimedItems", userId] });
      claimedItemsService.notifyClaimedUpdated(userId);
    },
  });
}

export function useUnclaimItem(userId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimedItemsService.unclaimItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claimedItems", userId] });
      claimedItemsService.notifyClaimedUpdated(userId);
      // This hook only has userId in scope (no nameId), and the mutation's variables carry
      // itemId rather than nameId, so there's no single wishlistItems cache to target without
      // an extra lookup (out of scope per the migration review). Invalidate every nameId's
      // wishlistItems cache broadly instead — coarser than the wishlistItems-side fix, but
      // correct and simple. No broadcast-notify call here either, since notifyItemsUpdated is
      // scoped to one nameId's topic and there's no single nameId to notify.
      queryClient.invalidateQueries({ queryKey: ["wishlistItems"] });
    },
  });
}

export function useTogglePurchased(userId) {
  return useClaimedItemsMutation(userId, claimedItemsService.togglePurchased);
}

// These two bypass the shared useClaimedItemsMutation helper (unlike useTogglePurchased)
// so their onSuccess can reach variables.recipientNameId and additionally refresh the
// recipient's own Wishlist cache — mirrors useClaimItem/useUnclaimItem in useWishlistItems.js.
export function useAddSurpriseItem(userId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimedItemsService.addSurpriseItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["claimedItems", userId] });
      claimedItemsService.notifyClaimedUpdated(userId);
      if (variables.recipientNameId) {
        queryClient.invalidateQueries({ queryKey: ["wishlistItems", variables.recipientNameId] });
        wishlistItemsService.notifyItemsUpdated(variables.recipientNameId);
      }
    },
  });
}

export function useDeleteSurpriseItem(userId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimedItemsService.deleteSurpriseItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["claimedItems", userId] });
      claimedItemsService.notifyClaimedUpdated(userId);
      if (variables.recipientNameId) {
        queryClient.invalidateQueries({ queryKey: ["wishlistItems", variables.recipientNameId] });
        wishlistItemsService.notifyItemsUpdated(variables.recipientNameId);
      }
    },
  });
}

export function useRecipientOptions(groupId, userId, enabled) {
  return useQuery({
    queryKey: ["claimedItemsRecipients", groupId, userId],
    queryFn: () => claimedItemsService.fetchRecipientOptions(groupId, userId),
    enabled: !!groupId && !!userId && !!enabled,
  });
}
